// Terreng-analyse over et høyde-rutenett (DEM): høydeprofil langs en linje,
// helning/eksposisjon (slope/aspect), siktlinje (line-of-sight) og synlighet
// (viewshed). Rene algoritmer — DEM-DATA mates inn (fra SRTM/DTED/tjeneste), på
// samme måte som tidevanns-motoren. Deterministisk + testbart.
import { haversineM } from './geo.js';
const VOID = -32768;
/** Høyde i celle [row,col] (null utenfor rutenettet eller hull). */
export function elevationAtCell(g, row, col) {
    if (row < 0 || col < 0 || row >= g.rows || col >= g.cols)
        return null;
    const v = g.data[row * g.cols + col];
    return v === VOID ? null : v;
}
/** Bilineær høyde ved (lat,lon) (null utenfor / ved hull). */
export function elevationAt(g, lat, lon) {
    const EPS = 1e-6;
    let fr = (g.north - lat) / g.latStep;
    let fc = (lon - g.west) / g.lonStep;
    if (fr < -EPS || fc < -EPS || fr > g.rows - 1 + EPS || fc > g.cols - 1 + EPS)
        return null;
    fr = Math.min(Math.max(fr, 0), g.rows - 1); // klem bort flyttalls-slark på kanten
    fc = Math.min(Math.max(fc, 0), g.cols - 1);
    const r0 = Math.floor(fr), c0 = Math.floor(fc);
    const r1 = Math.min(r0 + 1, g.rows - 1), c1 = Math.min(c0 + 1, g.cols - 1);
    const q11 = elevationAtCell(g, r0, c0), q12 = elevationAtCell(g, r0, c1);
    const q21 = elevationAtCell(g, r1, c0), q22 = elevationAtCell(g, r1, c1);
    if (q11 === null || q12 === null || q21 === null || q22 === null)
        return null;
    const dr = fr - r0, dc = fc - c0;
    const top = q11 * (1 - dc) + q12 * dc;
    const bot = q21 * (1 - dc) + q22 * dc;
    return top * (1 - dr) + bot * dr;
}
/** Høydeprofil langs linja from→to, `samples`+1 punkt (inkl. ende­punkter). */
export function heightProfile(g, from, to, samples = 100) {
    const total = haversineM(from, to);
    const out = [];
    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const lat = from.lat + (to.lat - from.lat) * t;
        const lon = from.lon + (to.lon - from.lon) * t;
        out.push({ distanceM: total * t, lat, lon, elevM: elevationAt(g, lat, lon) });
    }
    return out;
}
/** Helning + eksposisjon i celle [row,col] (Horns metode over 3×3-nabolag). */
export function slopeAspect(g, row, col) {
    const z = (r, c) => elevationAtCell(g, Math.max(0, Math.min(g.rows - 1, r)), Math.max(0, Math.min(g.cols - 1, c)));
    const a = z(row - 1, col - 1), b = z(row - 1, col), c = z(row - 1, col + 1);
    const d = z(row, col - 1), f = z(row, col + 1);
    const gg = z(row + 1, col - 1), h = z(row + 1, col), i = z(row + 1, col + 1);
    if ([a, b, c, d, f, gg, h, i].some((v) => v === null))
        return null;
    const dzdx = ((c + 2 * f + i) - (a + 2 * d + gg)) / (8 * g.cellSizeM);
    const dzdy = ((gg + 2 * h + i) - (a + 2 * b + c)) / (8 * g.cellSizeM);
    const slopeDeg = Math.atan(Math.hypot(dzdx, dzdy)) * 180 / Math.PI;
    let aspectDeg = Math.atan2(dzdy, -dzdx) * 180 / Math.PI;
    aspectDeg = (90 - aspectDeg + 360) % 360;
    return { slopeDeg, aspectDeg };
}
/**
 * Siktlinje: ser observatør (from + øyehøyde) målet (to + målhøyde)? Terrenget
 * mellom må ikke bryte den rette sikt­linjen. Sampler tett langs linja.
 */
export function lineOfSight(g, from, to, fromHeightM = 1.7, toHeightM = 0, samples = 100) {
    const e0 = elevationAt(g, from.lat, from.lon);
    const e1 = elevationAt(g, to.lat, to.lon);
    if (e0 === null || e1 === null)
        return false;
    const start = e0 + fromHeightM, end = e1 + toHeightM;
    for (let i = 1; i < samples; i++) {
        const t = i / samples;
        const lat = from.lat + (to.lat - from.lat) * t;
        const lon = from.lon + (to.lon - from.lon) * t;
        const terrain = elevationAt(g, lat, lon);
        if (terrain === null)
            continue;
        const sight = start + (end - start) * t;
        if (terrain > sight)
            return false; // terrenget blokkerer
    }
    return true;
}
/**
 * Viewshed: hvilke celler innen `radiusCells` ser observatøren? Returnerer et
 * bool-rutenett (samme dim). Enkel LOS per celle — O(n²·samples), passer taktiske
 * ruter (ikke landsdekkende). Observatør oppgis som celle-koordinat.
 */
export function viewshed(g, obsRow, obsCol, radiusCells, observerHeightM = 1.7) {
    const vis = Array.from({ length: g.rows }, () => new Array(g.cols).fill(false));
    const obsLat = g.north - obsRow * g.latStep;
    const obsLon = g.west + obsCol * g.lonStep;
    for (let r = Math.max(0, obsRow - radiusCells); r <= Math.min(g.rows - 1, obsRow + radiusCells); r++) {
        for (let c = Math.max(0, obsCol - radiusCells); c <= Math.min(g.cols - 1, obsCol + radiusCells); c++) {
            const lat = g.north - r * g.latStep;
            const lon = g.west + c * g.lonStep;
            vis[r][c] = lineOfSight(g, { lat: obsLat, lon: obsLon }, { lat, lon }, observerHeightM, 0, Math.max(8, radiusCells));
        }
    }
    return vis;
}
//# sourceMappingURL=terrain.js.map